import React from 'react';
import { Video, ExternalLink, FileText, Image as ImageIcon } from 'lucide-react';
import { AssessmentPill } from '../shared';
import { fmtDate } from '../constants';
import AuthImg from './AuthImg';
import { assetNavigationHref } from '../../../utils/assetOpener';

export const humanCategory = (c) => {
  if (!c) return '';
  return String(c).replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
};

const evidenceLabel = (ev) => ev.label
  || `${ev.source || 'Video'} Evidence${ev.timestamp ? ` — ${ev.timestamp}` : ''}`;

// One evidence row. kind: 'video' | 'link' | 'image' | 'file'. Renders a real
// <a> so the hyperlink survives Playwright PDF generation; never prints raw URLs.
export const EvidenceRow = ({ label, source, timestamp, url, kind = 'video', caption, fileName, testId = 'evidence' }) => {
  const href = url ? assetNavigationHref(url, fileName || label) : null;
  if (kind === 'image' && url) {
    return (
      <figure className="overflow-hidden rounded-lg border border-slate-200 bg-white report-section" data-testid={`${testId}-image`}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="block bg-slate-50 p-2" data-testid={`${testId}-open-image`}>
          <AuthImg src={url} alt={label || caption || 'Supporting image'} className="mx-auto max-h-72 w-auto max-w-full rounded object-contain"
          fallback={<div className="flex h-24 items-center justify-center rounded bg-slate-50 text-[11px] text-slate-400">Image unavailable</div>} />
        </a>
        {(label || caption) && <figcaption className="flex items-start gap-2 px-3 py-2 text-[11px] text-slate-500"><ImageIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{label}{label && caption ? ' — ' : ''}{caption}</span></figcaption>}
      </figure>
    );
  }
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid={`${testId}-${kind === 'file' ? 'file-card' : 'link-card'}`}>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-slate-800">{label}</div>
        <div className="text-[11px] text-slate-500">
          {source || (kind === 'link' ? 'Link' : kind === 'file' ? (fileName || 'Document') : 'Video')}{timestamp ? ` · ${timestamp}` : ''}
        </div>
        {caption && <div className="mt-0.5 text-[12px] text-slate-600">{caption}</div>}
      </div>
      {url && (
        <a href={href} target="_blank" rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[12px] font-medium text-sky-700 transition-colors hover:bg-sky-100"
          data-testid={`${testId}-open`}>
          {kind === 'link' ? <ExternalLink className="h-3.5 w-3.5" /> : kind === 'file' ? <FileText className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />}
          {kind === 'video' ? 'Watch Video' : 'Open Evidence'}
        </a>
      )}
    </div>
  );
};

// Report-owned supporting evidence array (Football Areas / Priorities /
// section evidence). Only items included in the final document are shown —
// items default to included; staff explicitly unticks to exclude.
export const SupportingEvidenceView = ({ items, testId = 'supporting-evidence' }) => {
  const list = (items || []).filter((e) => e && e.include_in_final !== false && (e.url || e.label || e.caption));
  if (list.length === 0) return null;
  return (
    <div className="mt-2" data-testid={testId}>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Supporting Evidence</div>
      <div className="space-y-1.5">
        {[...list].sort((a, b) => (a.order || 0) - (b.order || 0)).map((e, i) => (
          <EvidenceRow key={e.id || i} label={e.label || (e.kind === 'image' ? 'Image' : e.kind === 'file' ? (e.file_name || 'Document') : 'Evidence')} source={e.source} timestamp={e.timestamp} url={e.url} kind={e.kind || 'video'} caption={e.caption} fileName={e.file_name} testId={`${testId}-item-${e.id || i}`} />
        ))}
      </div>
    </div>
  );
};

// Match Analysis presentation — full observation + canonical Veo evidence.
export const MatchAnalysisView = ({ data, includedIds }) => {
  const all = (data && data.observations) || [];
  const list = includedIds ? all.filter((o) => includedIds.has(o.id)) : all;
  if (list.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="doc-match-analysis">
      {list.map((o, i) => {
        const ev = o.evidence || [];
        return (
          <div key={o.id || i} className="rounded-xl border border-slate-200 bg-white p-3 report-section" data-testid={`doc-observation-${o.id || i}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[14px] font-semibold text-slate-800">{humanCategory(o.category) || 'Observation'}</span>
              <AssessmentPill assessment={o.assessment} />
              {o.timestamp && <span className="text-[11px] text-slate-400">{o.timestamp}</span>}
              {o.observation_date && <span className="text-[11px] text-slate-400">· {fmtDate(o.observation_date)}</span>}
            </div>
            {o.development_tags && o.development_tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {o.development_tags.map((t) => t && (
                  <span key={t.id} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">{t.label}</span>
                ))}
              </div>
            )}
            {o.text && <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700" data-testid={`doc-observation-text-${o.id || i}`}>{o.text}</p>}
            {ev.length > 0 && (
              <div className="mt-2">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Video &amp; Supporting Evidence</div>
                <div className="space-y-1.5">
                  {ev.map((e, j) => (
                    <EvidenceRow key={e.id || j} label={evidenceLabel(e)} source={e.source} timestamp={e.timestamp} url={e.url} kind="video" testId={`match-evidence-${o.id || i}-${e.id || j}`} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const NOTE_ASSESSMENTS = {
  strength: 'Strength', on_track: 'On Track', developing: 'Developing',
  priority_development_area: 'Priority Development Area', not_assessed: 'Not Assessed',
};

export const PhysicalGpsAnalysisNotesView = ({ notes = [], evidence = [] }) => {
  const byId = new Map(evidence.filter((item) => item?.id).map((item) => [item.id, item]));
  const visible = [...notes].filter((note) => note && (note.title || note.observation || note.recommendation)).sort((a, b) => (a.order || 0) - (b.order || 0));
  if (visible.length === 0) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="doc-gps-analysis-notes">
      <div className="text-[12px] font-bold uppercase tracking-wide text-[color:var(--pfap-green)]">Physical / GPS Analysis</div>
      {visible.map((note, index) => {
        const linked = (note.linked_evidence_ids || []).map((id) => byId.get(id)).filter(Boolean);
        return (
          <article key={note.id || index} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 report-section" data-testid={`doc-gps-note-${index}`}>
            <div className="flex flex-wrap items-center justify-between gap-2"><h4 className="text-[14px] font-semibold text-slate-800">{note.title || 'Physical / GPS Analysis'}</h4>{note.assessment && <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">{NOTE_ASSESSMENTS[note.assessment] || note.assessment}</span>}</div>
            {note.observation && <div className="mt-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Observation</div><p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{note.observation}</p></div>}
            {note.recommendation && <div className="mt-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Recommendation</div><p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{note.recommendation}</p></div>}
            {linked.length > 0 && <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`doc-gps-note-evidence-${index}`}>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Linked evidence</span>
              {linked.map((item, linkedIndex) => item.url ? (
                <a key={item.id || linkedIndex} href={assetNavigationHref(item.url, item.file_name || item.label)} target="_blank" rel="noopener noreferrer" className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600" data-testid={`doc-gps-note-evidence-${index}-${linkedIndex}`}>{item.label || item.file_name || `Evidence ${linkedIndex + 1}`}</a>
              ) : <span key={item.id || linkedIndex} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600" data-testid={`doc-gps-note-evidence-${index}-${linkedIndex}`}>{item.label || item.file_name || `Evidence ${linkedIndex + 1}`}</span>)}
            </div>}
          </article>
        );
      })}
    </div>
  );
};
